// Dedicated full project page (/projects/:projectId).
// Header + JLL 7-phase stepper + two responsive cards:
//   left  "The record · PMO"      - record fields, financials, summary, children
//   right "Working updates · weekly" - see full/WeeklyColumn.jsx
// No PMO code is shown: there is no authoritative code field on the record.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { T, PROJECT_STATUS } from "../../app/tokens.js";
import {
  getProject, getProjectDecisions, getProjectDocuments, getProjectMilestones,
  getProjectRisks, getProjectSlackSource, getProjectSources,
} from "../../data/projectsClient.js";
import { AuthError, NotFoundError } from "../../data/errors.js";
import { fmtDate } from "../../utils/format.js";
import { buildProjectUrl } from "../../services/buildUrl.js";
import { useSensitivityPolicy } from "../../contexts/SensitivityPolicyContext.jsx";
import { Modal } from "../../components/Modal.jsx";
import { getProjectPhaseSteps } from "./projectFilters.js";
import { formatRecordAmount, recordVariance } from "./projectRecord.js";
import { typePillMeta } from "./briefing/landingModel.js";
import { ProjectEditModal } from "./ProjectEditPanel.jsx";
import { Connections, ProjectsIntelReviewPanel } from "./ProjectInlineExpanded.jsx";
import { MyWeeklyUpdate } from "./weekly/MyWeeklyUpdate.jsx";
import { InfoTip } from "./full/InfoTip.jsx";
import { MilestonesSection, RisksSection, DecisionsSection, DocumentsSection, sx as recSx } from "./full/RecordSections.jsx";
import { WeeklyColumn } from "./full/WeeklyColumn.jsx";
import { ProjectMediaSection } from "./media/ProjectMediaSection.jsx";
import { ProjectSchedule } from "./full/ProjectSchedule.jsx";
import { ProjectConnections } from "./connections/ProjectConnections.jsx";
import { RecordTabs, RecordTabPanel } from "./full/RecordTabs.jsx";
import { ProjectCategoryBudgets } from "./full/ProjectCategoryBudgets.jsx";
import "../../components/objectChrome.css";
import { DetailNavBand, DetailRecordHeader } from "../../components/DetailRecordChrome.jsx";

export const RECORD_TIP = "What's true now. PMO-owned; changes deliberately via Edit record.";
const RECORD_WRITE_ROLES = new Set(["admin", "editor"]);

/** "Region · City · Building · OfficeID" from actual record identity; blanks omitted. */
export function fullPageIdentity(project) {
  return [project?.locationRegion, project?.locationName, project?.locationAlias, project?.locationId]
    .filter((v) => v != null && String(v).trim() !== "")
    .filter((v, i, arr) => arr.indexOf(v) === i)
    .join(" · ");
}

/**
 * Only the server's verified import-audit projection establishes a CapEx
 * match. Neither names nor the Financials lens's portfolio caption prove it.
 */
export function financialSourceCaption(project) {
  return project?.financialProvenance?.evidence === "matching_financial_import_audit"
    ? "source: CapEx 2026 Working Doc"
    : "source: project record - no matching CapEx import recorded";
}

export function classifyLoadError(err) {
  if (err instanceof NotFoundError || err?.status === 404) return "missing";
  if (err instanceof AuthError || err?.status === 401 || err?.status === 403) return "denied";
  return "failed";
}

const loadEffectiveSources = (id) => getProjectSources(id, { effective: true });

const IDLE = { status: "loading", items: [], error: null };
const DISABLED = { status: "idle", items: [], error: null };

// `enabled` (default true) lets recordOnly skip sources it never renders.
function useCollection(projectId, loader, token, enabled = true) {
  const [state, setState] = useState(enabled ? IDLE : DISABLED);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    if (!enabled) { setState(DISABLED); return undefined; }
    let active = true;
    setState(IDLE);
    Promise.resolve().then(() => loader(projectId))
      .then((rows) => { if (!Array.isArray(rows)) throw new Error("The server returned an invalid list."); if (active) setState({ status: "ok", items: rows, error: null }); })
      .catch((error) => { if (active) setState({ status: "error", items: [], error }); });
    return () => { active = false; };
  }, [projectId, loader, token, retry, enabled]);
  const update = useCallback((fn) => setState((s) => ({ ...s, items: typeof fn === "function" ? fn(s.items) : fn })), []);
  return [state, update, () => setRetry((n) => n + 1)];
}

function PhaseStepper({ phase }) {
  const steps = getProjectPhaseSteps(phase);
  const unknown = steps.every((s) => s.state === "upcoming");
  const complete = steps.every((s) => s.state === "done");
  return (
    <section aria-label="Project phase" data-testid="full-phase-stepper" style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, padding: "18px 22px", marginBottom: 14 }}>
      <div style={{ overflowX: "auto" }}>
        <ol style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gridTemplateColumns: `repeat(${steps.length}, minmax(76px, 1fr))`, minWidth: 540 }}>
          {steps.map((s, i) => {
            const bg = s.state === "done" ? T.projectsHubNavy : s.state === "current" ? T.blue : T.borderDark;
            return (
              <li key={s.key} data-testid={`full-phase-${s.key}`} data-state={s.state} aria-current={s.state === "current" ? "step" : undefined} style={{ position: "relative", display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                {i > 0 && <span aria-hidden="true" style={{ position: "absolute", top: 9, right: "50%", width: "100%", height: 2, background: s.state === "upcoming" ? T.border : T.projectsHubNavy }} />}
                <span aria-hidden="true" style={{ position: "relative", zIndex: 1, width: 19, height: 19, borderRadius: "50%", background: bg, border: `3px solid ${T.card}`, boxSizing: "border-box", boxShadow: s.state === "current" ? `0 0 0 3px ${T.blueSoft}` : "none" }} />
                <span style={{ fontSize: T.fsXs, fontWeight: T.fwBold, textAlign: "center", color: s.state === "upcoming" ? T.mid : T.dark }}>
                  {s.label}
                  <span style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }}>{s.state === "done" ? " (completed)" : s.state === "current" ? " (current)" : ""}</span>
                </span>
              </li>
            );
          })}
        </ol>
      </div>
      {(unknown || complete) && (
        <div data-testid="full-phase-note" style={{ marginTop: 10, fontSize: T.fsXs, color: complete ? T.green : T.mid, textAlign: "center" }}>
          {complete ? "Project complete" : `Phase not mapped${phase ? ` (recorded: ${phase})` : " (not recorded)"}`}
        </div>
      )}
    </section>
  );
}

function FieldCell({ label, children, testId, tone }) {
  return (
    <div data-testid={testId} style={{ minWidth: 0 }}>
      <div style={{ fontSize: T.fsXs, color: T.mid }}>{label}</div>
      <div style={{ fontSize: T.fsBase, fontWeight: T.fwBold, color: tone || T.dark, overflowWrap: "anywhere" }}>{children}</div>
    </div>
  );
}

function PageState({ kind, error, onRetry, backLink }) {
  const copy = {
    missing: ["Project not found", "This project does not exist or was removed."],
    denied: ["You don't have access to this project", "Ask a PMO admin if you need to see it."],
    failed: ["Couldn't load this project", error?.message || "The request failed."],
  }[kind];
  return (
    <div data-testid={`full-page-${kind}`} role={kind === "failed" ? "alert" : "status"} style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, padding: "36px 28px", textAlign: "center" }}>
      <div style={{ fontSize: T.fsMd, fontWeight: T.fwBold, color: T.projectsHubNavy, marginBottom: 6 }}>{copy[0]}</div>
      <div style={{ fontSize: T.fsSm, color: T.mid, marginBottom: 14 }}>{copy[1]}</div>
      <div style={{ display: "flex", justifyContent: "center", gap: 10 }}>
        {kind === "failed" && <button type="button" onClick={onRetry} data-testid="full-page-retry" style={{ border: `1px solid ${T.border}`, background: T.card, borderRadius: 8, padding: "7px 13px", fontSize: T.fsXs, fontWeight: T.fwBold, color: T.projectsHubNavy, cursor: "pointer" }}>Retry</button>}
        {backLink}
      </div>
    </div>
  );
}

function Skeleton() {
  const bar = (w, h = 14) => <div style={{ height: h, width: w, borderRadius: 6, background: T.lightGray, marginBottom: 10 }} />;
  return (
    <div data-testid="full-page-loading" aria-busy="true">
      {bar("38%", 24)}{bar("24%")}
      <div style={{ height: 76, borderRadius: 12, background: T.card, border: `1px solid ${T.border}`, margin: "14px 0" }} />
      <div className="full-cols">
        <div style={{ height: 420, borderRadius: 12, background: T.card, border: `1px solid ${T.border}` }} />
        <div style={{ height: 420, borderRadius: 12, background: T.card, border: `1px solid ${T.border}` }} />
      </div>
    </div>
  );
}

export function ProjectFullPage({ projectId, currentRole = "viewer", backHref = "/projects", onBack, onOpenWeeklyArchive, headerSearch, headerRightSlot, searchOpen = false, recordOnly = false, readOnly = false, onRecordSaved, onArchived, onEditingChange, onEditorStateChange, recordTabResetToken = 0 }) {
  const [load, setLoad] = useState({ status: "loading", project: null, error: null });
  const [loadToken, setLoadToken] = useState(0);
  const [editing, setEditing] = useState(false);
  const [weeklyRefresh, setWeeklyRefresh] = useState(0);
  const [myUpdate, setMyUpdate] = useState(false);
  const [pending, setPending] = useState(false);
  const [closeAsk, setCloseAsk] = useState(false);
  const [documentsToken, setDocumentsToken] = useState(0);
  const [slackSource, setSlackSource] = useState(null);
  const archiveCache = useRef(new Map());
  const sensitivity = useSensitivityPolicy();
  const canSeeBudget = sensitivity.canSee("projects.budget");
  // recordOnly: just "The record · PMO" composition (stepper, schedule, record
  // card) for reuse inside Updates; readOnly removes every record edit path.
  const canEdit = RECORD_WRITE_ROLES.has(currentRole) && !readOnly;
  const scheduleRole = readOnly ? "viewer" : currentRole;
  const savedRef = useRef(onRecordSaved);
  savedRef.current = onRecordSaved;
  const editingRef = useRef(onEditingChange);
  editingRef.current = onEditingChange;
  useEffect(() => { editingRef.current?.(editing); }, [editing]);
  // Aggregate record-editor state ({ editing, busy }) across the edit modal,
  // schedule and every record section; owners (record drawer) block close,
  // switch and navigation on it. Callback held in a ref.
  const editorStates = useRef(new Map());
  const editorCb = useRef(onEditorStateChange);
  editorCb.current = onEditorStateChange;
  const [modalBusy, setModalBusy] = useState(false);
  const emitEditorState = useCallback(() => {
    let anyEditing = false; let anyBusy = false;
    for (const st of editorStates.current.values()) { anyEditing ||= st.editing; anyBusy ||= st.busy; }
    editorCb.current?.({ editing: anyEditing, busy: anyBusy });
  }, []);
  const reporters = useMemo(() => Object.fromEntries(["schedule", "milestones", "risks", "decisions", "documents", "budget"].map((k) => [k, (st) => { editorStates.current.set(k, st); emitEditorState(); }])), [emitEditorState]);
  useEffect(() => { editorStates.current.set("modal", { editing: editing && canEdit, busy: modalBusy }); emitEditorState(); }, [editing, canEdit, modalBusy, emitEditorState]);

  useEffect(() => {
    let active = true;
    setLoad((l) => ({ status: "loading", project: l.project?.id === projectId ? l.project : null, error: null }));
    Promise.resolve().then(() => getProject(projectId))
      .then((p) => { if (active) setLoad(p ? { status: "ok", project: p, error: null } : { status: "missing", project: null, error: null }); })
      .catch((error) => { if (active) setLoad({ status: classifyLoadError(error), project: null, error }); });
    if (!recordOnly) Promise.resolve().then(() => getProjectSlackSource(projectId)).then((s) => { if (active) setSlackSource(s); }).catch(() => { if (active) setSlackSource(null); });
    return () => { active = false; };
  }, [projectId, loadToken, recordOnly]);

  const [milestones, setMilestones, retryMilestones] = useCollection(projectId, getProjectMilestones, 0);
  const [risks, setRisks, retryRisks] = useCollection(projectId, getProjectRisks, 0, !recordOnly);
  const [decisions, setDecisions, retryDecisions] = useCollection(projectId, getProjectDecisions, 0, !recordOnly);
  // Typed source links (project-owned + inherited portfolio sources).
  const [sources, setSources, retrySources] = useCollection(projectId, loadEffectiveSources, 0, !recordOnly);
  const [documents, setDocuments, retryDocuments] = useCollection(projectId, getProjectDocuments, documentsToken, !recordOnly);

  // recordOnly tabs: Overview default, reset per project / drawer open; the
  // Financials tab exists only while projects.budget is visible.
  const [recordTab, setRecordTab] = useState("overview");
  useEffect(() => { setRecordTab("overview"); }, [projectId, recordTabResetToken]);
  useEffect(() => { if (!canSeeBudget) setRecordTab((t) => (t === "financials" ? "overview" : t)); }, [canSeeBudget]);
  // The budget child clears drafts on unmount, but keeps reporting an in-flight
  // write until it settles even when financial access is revoked.

  const project = load.project;

  // Derived dates (next milestone, FDOB) are server-owned; refresh the record
  // after milestone writes so header/fields stay consistent without duplicates.
  const [refreshError, setRefreshError] = useState(null);
  const refreshRecord = useCallback(() => {
    setRefreshError(null);
    Promise.resolve().then(() => getProject(projectId))
      .then((p) => { if (p) { setLoad({ status: "ok", project: p, error: null }); savedRef.current?.(p); } })
      .catch((err) => setRefreshError(err?.message || "Could not refresh the project record."));
  }, [projectId]);
  const onMilestonesChanged = useCallback((fn) => {
    setMilestones(fn);
    refreshRecord();
  }, [setMilestones, refreshRecord]);

  const backLink = (
    <a
      href={backHref}
      data-testid="link-back-projects"
      onClick={(e) => { if (onBack && !e.metaKey && !e.ctrlKey && !e.shiftKey && e.button === 0) { e.preventDefault(); onBack(); } }}
      style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: T.fsSm, fontWeight: T.fwBold, color: T.blue, textDecoration: "none" }}
    >
      <span aria-hidden="true">&larr;</span> Projects
    </a>
  );

  const closeMyUpdate = () => {
    if (pending) { setCloseAsk(true); return; }
    setCloseAsk(false); setMyUpdate(false); setWeeklyRefresh((n) => n + 1);
  };

  const status = project ? (project.phase === "complete" ? { label: "Complete", color: T.mid, bg: T.lightGray } : PROJECT_STATUS[project.statusOverall]) : null;
  const type = project ? typePillMeta(project.projectType) : null;
  const identity = project ? fullPageIdentity(project) : "";
  const variance = project ? recordVariance(project) : null;
  const officeText = project ? [project.locationName, project.locationAlias].filter(Boolean).join(" · ") || project.locationId : null;
  const headerActions = project && (
    <>
      {sources.status === "error" && (
        <span role="alert" data-testid="full-sources-error" style={{ fontSize: T.fsXs, color: T.red }}>
          Source links unavailable: {sources.error?.message || "request failed"}
          <button type="button" onClick={retrySources} style={{ ...recSx.addLink, color: T.red, marginLeft: 6 }}>Retry</button>
        </span>
      )}
      <Connections projectId={projectId} project={project} documents={documents.items} slackSource={slackSource} canEdit={canEdit}
        sourceData={sources.status === "ok" ? sources.items : null}
        onSourcesChange={setSources} onChange={(fn) => { setDocuments(fn); setDocumentsToken((n) => n + 1); }} onProjectUpdated={(p) => p && setLoad({ status: "ok", project: { ...project, ...p }, error: null })} />
    </>
  );

  const recordPre = project && (
    <>
            {refreshError && (
              <div role="alert" data-testid="full-record-refresh-error" style={{ ...recSx.err, marginTop: 0, marginBottom: 12 }}>
                Saved, but the record could not be refreshed: {refreshError}
                <button type="button" onClick={refreshRecord} data-testid="full-record-refresh-retry" style={{ ...recSx.addLink, color: T.red, marginLeft: 8 }}>Retry</button>
              </div>
            )}

            <PhaseStepper phase={project.phase} />

            <ProjectSchedule key={projectId} projectId={projectId}
              project={{ ...project, nextMilestone: milestones.status === "ok" ? milestones.items.find(m => m.status !== "complete") ?? null : null }}
              currentRole={scheduleRole} onEditorState={reporters.schedule} onSaved={() => { refreshRecord(); retryMilestones(); }} />

    </>
  );
  const infoBlock = project && (
    <>
                  <div style={recSx.sec}>Project info</div>
                  <div className="full-grid2" style={{ marginBottom: 16 }}>
                    <FieldCell label="Office" testId="full-field-office">
                      {project.locationId ? <a href={`?actionNav=location&actionNavId=${encodeURIComponent(project.locationId)}`} style={{ color: T.blue, textDecoration: "none" }}>{officeText}</a> : "Not set"}
                    </FieldCell>
                    <FieldCell label="Type" testId="full-field-type">{type.label}</FieldCell>
                    <FieldCell label="Owner" testId="full-field-owner">{project.internalOwner || "Not set"}</FieldCell>
                    <FieldCell label="JLL owner" testId="full-field-jll-owner">{project.jllOwner || "Not set"}</FieldCell>
                    <FieldCell label="Start date" testId="full-field-start">{fmtDate(project.startDate) || "Not set"}</FieldCell>
                    <FieldCell label="FDOB" testId="full-field-fdob">{project.fdobDate ? fmtDate(project.fdobDate) : "Not recorded"}</FieldCell>
                  </div>

    </>
  );
  const financialSummary = project && canSeeBudget && (
                    <div data-testid="full-financials">
                      <div style={recSx.sec}>
                        <span>Financials · <span data-testid="full-financials-source" style={{ color: T.light, fontWeight: T.fwSemibold, textTransform: "none", letterSpacing: 0 }}>{financialSourceCaption(project)}</span></span>
                      </div>
                      <div className="full-grid2" style={{ background: T.hoverBg, border: `1px solid ${T.border}`, borderRadius: 10, padding: "13px 15px", marginBottom: 16, gap: "14px 20px" }}>
                        {[
                          ["budget", "Budget", project.approvedBudget, false],
                          ["spend", "Spend-to-date", project.spentToDate, false],
                          ["forecast", "Forecast at completion", project.forecastAtCompletion, false],
                          ["variance", "Variance (Budget - Forecast)", variance, true],
                        ].map(([key, label, value, signed]) => (
                          <FieldCell key={key} label={label} testId={`full-fin-${key}`} tone={key === "variance" && variance != null ? (variance < 0 ? T.red : T.green) : value == null ? T.mid : T.dark}>
                            <span style={{ fontSize: T.fsMd, fontWeight: T.fwHubTitle }}>{formatRecordAmount(value, project.currency, signed)}</span>
                          </FieldCell>
                        ))}
                      </div>
                    </div>
  );
  const summaryBlock = project && (
    <>
                  <div style={recSx.sec}>Summary</div>
                  <div data-testid="full-record-summary" style={{ background: T.hoverBg, borderRadius: 8, padding: "11px 13px", fontSize: T.fsSm, color: project.oneLineSummary ? T.dark : T.mid, fontStyle: project.oneLineSummary ? "normal" : "italic", marginBottom: 16, whiteSpace: "pre-wrap" }}>
                    {project.oneLineSummary || "No summary recorded."}
                  </div>

    </>
  );
  const milestonesBlock = project && (
<MilestonesSection onEditorState={reporters.milestones} projectId={projectId} state={milestones} canEdit={canEdit} onChanged={onMilestonesChanged} onRetry={retryMilestones} />
  );
  const recordCard = project && (
<section data-testid="full-record-card" aria-labelledby="full-record-title" style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "14px 18px", borderBottom: `1px solid ${T.border}` }}>
                  <h2 id="full-record-title" style={{ margin: 0, fontSize: T.fsXs, fontWeight: T.fwHubTitle, letterSpacing: ".05em", textTransform: "uppercase", color: T.projectsHubNavy, display: "flex", alignItems: "center" }}>
                    The record · PMO
                    <InfoTip text={RECORD_TIP} label="About the record" testId="full-record-info" />
                  </h2>
                  {canEdit && <button type="button" data-testid="full-edit-record" onClick={() => setEditing(true)} style={{ background: T.projectsHubNavy, color: T.white, border: `1px solid ${T.projectsHubNavy}`, borderRadius: 8, padding: "7px 13px", fontSize: T.fsXs, fontWeight: T.fwBold, cursor: "pointer" }}>Edit record</button>}
                </div>
                <div style={{ padding: "16px 18px" }}>
                  {infoBlock}
                  {canSeeBudget && financialSummary}
                  {summaryBlock}
                  {milestonesBlock}
                  <div style={{ ...recSx.sec, marginTop: 18 }}>Risks, decisions &amp; documents</div>
                  <RisksSection onEditorState={reporters.risks} projectId={projectId} state={risks} canEdit={canEdit} onChanged={(fn) => { setRisks(fn); if (recordOnly) refreshRecord(); }} onRetry={retryRisks} />
                  <DecisionsSection onEditorState={reporters.decisions} projectId={projectId} state={decisions} canEdit={canEdit} onChanged={(fn) => { setDecisions(fn); if (recordOnly) refreshRecord(); }} onRetry={retryDecisions} />
                  <DocumentsSection onEditorState={reporters.documents} projectId={projectId} state={documents} canEdit={canEdit} onChanged={setDocuments} onRetry={retryDocuments} />
                  {!recordOnly && <ProjectMediaSection key={projectId} projectId={projectId} allowManage={canEdit} />}
                </div>
              </section>
  );
  const editModal = project && (
<ProjectEditModal
              isOpen={editing && canEdit}
              mode="edit"
              project={project}
              onClose={() => setEditing(false)}
              onBusyChange={setModalBusy}
              guardUnsaved={recordOnly}
              canSeeBudget={!recordOnly || canSeeBudget}
              canArchive={currentRole === "admin" && canEdit}
              onArchived={(id) => {
                editorStates.current.clear();
                editorCb.current?.({ editing: false, busy: false });
                editingRef.current?.(false);
                setEditing(false);
                setLoad({ status: "missing", project: null, error: null });
                onArchived?.(id);
              }}
              onSaved={(updated) => { if (updated) { setLoad({ status: "ok", project: updated, error: null }); savedRef.current?.(updated); } setEditing(false); retryMilestones(); }}
            />
  );

  if (recordOnly) {
    const recordTabs = [{ key: "overview", label: "Overview" }, { key: "timeline", label: "Timeline" }, ...(canSeeBudget ? [{ key: "financials", label: "Financials" }] : [])];
    const recordOnlyCard = { background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, minWidth: 0 };
    const recordOnlyHead = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "14px 18px", borderBottom: `1px solid ${T.border}` };
    const recordOnlyTitle = { margin: 0, fontSize: T.fsXs, fontWeight: T.fwHubTitle, letterSpacing: ".05em", textTransform: "uppercase", color: T.projectsHubNavy, display: "flex", alignItems: "center" };
    return (
      <div data-testid="project-record-only" className="full-record-only">
        <style>{`
          .full-record-only .full-grid2 { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px 20px; }
          .full-record-only .full-reveal { opacity: 0; transition: opacity .12s; }
          .full-record-only .full-mrow:hover .full-reveal, .full-record-only .full-mrow:focus-within .full-reveal, .full-record-only .full-reveal:focus-visible { opacity: 1; }
          @media (hover: none) { .full-record-only .full-reveal { opacity: 1; } }
          @media (max-width: 420px) { .full-record-only .full-grid2 { grid-template-columns: minmax(0, 1fr); } }
          .full-record-only [data-testid="project-cat-budget-editor"] > div:first-child { grid-template-columns: repeat(auto-fit, minmax(100px, 1fr)) !important; }
          .full-record-only [data-testid="project-cat-budget-editor"] label { min-width: 0; }
        `}</style>
        {load.status === "loading" && !project && <Skeleton />}
        {(load.status === "missing" || load.status === "denied" || load.status === "failed") && (
          <PageState kind={load.status} error={load.error} onRetry={() => setLoadToken((n) => n + 1)} backLink={null} />
        )}
        {project && (
          <>
            {refreshError && (
              <div role="alert" data-testid="full-record-refresh-error" style={{ ...recSx.err, marginTop: 0, marginBottom: 12 }}>
                Saved, but the record could not be refreshed: {refreshError}
                <button type="button" onClick={refreshRecord} data-testid="full-record-refresh-retry" style={{ ...recSx.addLink, color: T.red, marginLeft: 8 }}>Retry</button>
              </div>
            )}
            <RecordTabs tabs={recordTabs} active={recordTab} onChange={setRecordTab} />
            <RecordTabPanel tabKey="overview" active={recordTab}>
              <section data-testid="full-record-card" aria-labelledby="full-record-title" style={recordOnlyCard}>
                <div style={recordOnlyHead}>
                  <h2 id="full-record-title" style={recordOnlyTitle}>
                    Overview
                    <InfoTip text={RECORD_TIP} label="About the record" testId="full-record-info" />
                  </h2>
                  {canEdit && <button type="button" data-testid="full-edit-record" onClick={() => setEditing(true)} style={{ background: T.projectsHubNavy, color: T.white, border: `1px solid ${T.projectsHubNavy}`, borderRadius: 8, padding: "7px 13px", fontSize: T.fsXs, fontWeight: T.fwBold, cursor: "pointer" }}>Edit record</button>}
                </div>
                <div style={{ padding: "16px 18px" }}>
                  {infoBlock}
                  {summaryBlock}
                  {milestonesBlock}
                  <ProjectConnections connections={project.connections}
                    updatesHref={buildProjectUrl(project.id, typeof window === "undefined" ? "" : window.location.search)} />
                </div>
              </section>
            </RecordTabPanel>
            <RecordTabPanel tabKey="timeline" active={recordTab}>
              <PhaseStepper phase={project.phase} />
              <ProjectSchedule key={projectId} projectId={projectId}
                project={{ ...project, nextMilestone: milestones.status === "ok" ? milestones.items.find(m => m.status !== "complete") ?? null : null }}
                currentRole={scheduleRole} onEditorState={reporters.schedule} onSaved={() => { refreshRecord(); retryMilestones(); }} />
            </RecordTabPanel>
            {canSeeBudget && (
              <RecordTabPanel tabKey="financials" active={recordTab}>
                <section data-testid="record-financials-card" aria-label="Financials" style={recordOnlyCard}>
                  <div style={{ padding: "16px 18px" }}>
                    {financialSummary}
                    <ProjectCategoryBudgets projectId={projectId} project={project} canEdit={canEdit} onEditorState={reporters.budget} />
                  </div>
                </section>
              </RecordTabPanel>
            )}
            {editModal}
          </>
        )}
      </div>
    );
  }

  return (
    <div data-testid="project-full-page" className="object-detail-layout" style={{ background: T.tint, boxSizing: "border-box" }}>
      <style>{`
        .full-wrap { max-width: 1120px; margin: 0 auto; padding-inline: 22px; padding-bottom: 70px; }
        .full-cols { display: grid; grid-template-columns: minmax(0, 1.15fr) minmax(0, 1fr); gap: 16px; align-items: start; }
        .full-grid2 { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px 20px; }
        .full-reveal { opacity: 0; transition: opacity .12s; }
        .full-mrow:hover .full-reveal, .full-mrow:focus-within .full-reveal, .full-reveal:focus-visible { opacity: 1; }
        @media (hover: none) { .full-reveal { opacity: 1; } }
        @media (max-width: 860px) { .full-cols { grid-template-columns: minmax(0, 1fr); } .full-wrap { padding-inline: 14px; padding-bottom: 48px; } }
        @media (max-width: 420px) { .full-grid2 { grid-template-columns: minmax(0, 1fr); } }
      `}</style>
      <DetailRecordHeader data-testid="project-detail-title" headerSearch={headerSearch} headerRightSlot={headerRightSlot} actions={headerActions} searchOpen={searchOpen}>
        {project ? (
          <>
              <div className="object-title-content v2-detail-identity" style={{ minWidth: 0, flex: "1 1 320px", paddingLeft: "max(22px, calc((100cqw - 1120px) / 2 + 22px))" }}>
                <h1 data-testid="full-project-name" className="object-title-primary" style={{ fontSize: T.fsLgX6, fontWeight: T.fwHubTitle, color: T.projectsHubNavy, margin: "0 0 4px", display: "flex", alignItems: "center", gap: 11, lineHeight: 1.25 }}>
                  <span className="object-title-text">{project.name}</span>
                  <span data-testid="full-type-pill" style={{ fontSize: T.fsXs, fontWeight: T.fwBold, borderRadius: 7, padding: "3px 9px", color: type.fg, background: type.bg }}>{type.label}</span>
                  {status && <span data-testid="full-status-chip" style={{ fontSize: T.fsXs, fontWeight: T.fwBold, borderRadius: 999, padding: "3px 11px", color: status.color, background: status.bg }}>{status.label}</span>}
                </h1>
                {identity && <div data-testid="full-identity" className="object-title-meta" style={{ fontSize: T.fsSm, color: T.mid }}>{identity}</div>}
              </div>
          </>
        ) : <div className="object-title-content">{load.status === "loading" ? "Loading…" : "Projects"}</div>}
      </DetailRecordHeader>
      <DetailNavBand data-testid="project-detail-nav" className="object-detail-nav-flow">
        <div style={{ maxWidth: 1120, width: "100%", margin: "0 auto", paddingInline: 22 }}>{backLink}</div>
      </DetailNavBand>
      <div className="object-detail-body">
      <div className="full-wrap object-detail-entry">
        {load.status === "loading" && !project && <Skeleton />}
        {(load.status === "missing" || load.status === "denied" || load.status === "failed") && (
          <PageState kind={load.status} error={load.error} onRetry={() => setLoadToken((n) => n + 1)} backLink={null} />
        )}
        {project && (
          <>

            {project && recordPre}

            <ProjectsIntelReviewPanel projectId={projectId} currentRole={currentRole} onProjectUpdated={(p) => p && setLoad({ status: "ok", project: { ...project, ...p }, error: null })} onMilestoneChanged={() => { retryMilestones(); onMilestonesChanged((x) => x); }} />

            <div className="full-cols">
              {project && recordCard}
              <WeeklyColumn
                projectId={projectId}
                refreshToken={weeklyRefresh}
                onRefresh={() => setWeeklyRefresh((n) => n + 1)}
                onPost={() => setMyUpdate(true)}
                archiveCache={archiveCache.current}
                onOpenArchive={onOpenWeeklyArchive}
                canRequestAssignments={RECORD_WRITE_ROLES.has(currentRole)}
              />
            </div>

            {project && editModal}

            <Modal isOpen={myUpdate} onClose={closeMyUpdate} maxWidth={720} title="My weekly update" ariaLabelledBy="my-weekly-update-title" testId="my-weekly-update-dialog">
              {closeAsk && (
                <div role="alert" data-testid="my-update-close-ask" style={{ display: "flex", gap: 8, alignItems: "center", padding: "8px 18px", background: T.utilAmber, fontSize: T.fsSm }}>
                  <span style={{ flex: 1 }}>Changes are still saving. Closing now can cancel saves that have not started; unsent drafts are kept for this session.</span>
                  <button type="button" data-testid="my-update-close-keep" onClick={() => setCloseAsk(false)} style={{ border: `1px solid ${T.border}`, background: T.card, borderRadius: 6, padding: "3px 10px", cursor: "pointer" }}>Keep open</button>
                  <button type="button" data-testid="my-update-close-anyway" onClick={() => { setCloseAsk(false); setPending(false); setMyUpdate(false); setWeeklyRefresh((n) => n + 1); }} style={{ border: `1px solid ${T.border}`, background: T.card, borderRadius: 6, padding: "3px 10px", cursor: "pointer" }}>Close anyway</button>
                </div>
              )}
              {myUpdate && <div style={{ maxHeight: "78vh", overflowY: "auto" }}><MyWeeklyUpdate focusProjectId={projectId} onChanged={() => setWeeklyRefresh((n) => n + 1)} onPendingChange={setPending} currentRole={currentRole} /></div>}
            </Modal>
          </>
        )}
      </div>
      </div>
    </div>
  );
}

export default ProjectFullPage;
